import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminSyriatelTransactions, getAdminMtnTransactions, getAdminAllTransactions } from '../../API/ProtectedAuth';

export const useAdminTransactions = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get initial page from URL
    const urlPage = parseInt(searchParams.get('page')) || 1;

    const [activeTab, setActiveTab] = useState('all'); // 'syriatel' | 'mtn' | 'all'
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        page: urlPage,
        page_size: 20,
        search: '',
        created_at: '',
        date_from: '',
        date_to: '',
        operation_type: '',
        api_status: '',
        is_debt: undefined,
    });

    // Synchronize filters.page with URL changes (Back/Forward button)
    useEffect(() => {
        const pageFromUrl = parseInt(searchParams.get('page')) || 1;
        if (pageFromUrl !== filters.page) {
            setFilters(prev => ({ ...prev, page: pageFromUrl }));
        }
    }, [searchParams, filters.page]);

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let apiCall;
            if (activeTab === 'syriatel') {
                apiCall = getAdminSyriatelTransactions;
            } else if (activeTab === 'mtn') {
                apiCall = getAdminMtnTransactions;
            } else {
                apiCall = getAdminAllTransactions;
            }

            const response = await apiCall(filters);

            setTransactions(response.transactions);
            setTotal(response.total);
            setSummary(response.summary);
        } catch (err) {
            setError(err.message || 'فشل في تحميل البيانات');
            console.error("Error fetching transactions:", err);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // Helper to update URL param safely
    const updateUrlParams = useCallback((page) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (page > 1) {
                next.set('page', page.toString());
            } else {
                next.delete('page');
            }
            return next;
        });
    }, [setSearchParams]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        updateUrlParams(1);
        setFilters(prev => ({ ...prev, page: 1 })); // Reset page on tab change
    };

    const handleFilterChange = (newFilters) => {
        updateUrlParams(1);
        setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        updateUrlParams(newPage);
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    return {
        activeTab,
        transactions,
        summary,
        total,
        loading,
        error,
        filters,
        setActiveTab: handleTabChange,
        setFilters: handleFilterChange,
        setPage: handlePageChange,
        refresh: fetchTransactions
    };
};
